import React, { useEffect, useState } from 'react';
import { Button, Card, Box, Flex, AlertDialog, Spinner } from '@radix-ui/themes';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import usePaystackUpgrade from '../hooks/usePaystackUpgrade';
import EmailUpdateForm from '../components/forms/EmailUpdateForm';
import ConfirmPaymentDialog from '../components/ConfirmPaymentDialog';
import { updatePaymentDetailsLoading } from '../redux/slices/accountSlice';

const Payment = () => {
    const dispatch = useDispatch();

    const user = useSelector(state => state.user);
    const account = useSelector(state => state.account);

    const {
        upgradeStudent,
        showEmailDialog,
        setShowEmailDialog,
        showConfirmPaymentDialog,
        setShowConfirmPaymentDialog,
        baseAmount,
        discountCode,
        setDiscountCode,
        isLoading,
        applyDiscountCode,
        handleConfirmPaymentOpenChange,
        selectedDuration,
        setSelectedDuration,
    } = usePaystackUpgrade();

    const baseStudentData = {
        id: account?.details?.id,
        user: { email: user?.details?.email },
        payerType: 'student'
    };

    const handleUpgradeClick = () => {
        upgradeStudent(baseStudentData);
    };

    if (!user || !account) {
        return <div>Loading account details...</div>;
    }


    useEffect(() => {
        updatePaymentDetailsLoading(false);
    }, [account.paymentDetails])

    return (
        <>
            <div className="flex flex-col items-center gap-4 p-6">
                <h1 className="text-2xl font-bold">Upgrade to Advantage Plan</h1>
                <p className="text-base">Enjoy an enhanced experience, premium features, and support.</p>
                <Flex direction="row" gap="4" justify="center" align="stretch">
                    <Card p="4" height="100%">
                        <h2 className="text-xl font-semibold text-center mb-2">Free Plan</h2>
                        <p className="text-gray-600 font-bold text-center mb-6 text-xl">Free</p>
                        <p className="text-gray-600">Enjoy basic features at no cost.</p>
                        <ul className="list-disc pl-5 mt-4 space-y-2">
                            <li>Science only</li>
                            <li>50 questions daily</li>
                        </ul>
                    </Card>
                    <Card>
                        <h2 className="text-xl font-semibold text-center mb-2">Advantage Plan</h2>

                        {account.loading ? (
                            <div className='flex justify-center items-center mt-4'>
                                <Spinner size={16} />
                            </div>

                        ) : (
                            <p className="font-bold text-center mb-6 text-xl text-green-600">
                                {account?.paymentDetails?.payment_setting?.charge_amount ? `GHC ${account?.paymentDetails?.payment_setting?.charge_amount}` : 'GHC 40'}
                            </p>

                        )}

                        <p className="text-gray-600">Get premium features and support.</p>
                        <ul className="list-disc pl-5 mt-4 space-y-2">
                            <li>All Subjects</li>
                            <li>50 questions daily</li>
                        </ul>
                        <div className="mt-9">


                            {account.loading ? (
                                <div className='flex justify-center items-center mt-4'>
                                    <Spinner size={16} />
                                </div>

                            ) : (
                                <>
                                    {account.paymentDetails?.status === 'active' ? (
                                        <div className="flex flex-col gap-3">

                                            <Button
                                                onClick={handleUpgradeClick}
                                                className={`w-full ${account?.paymentDetails?.disable_payment ? 'bg-gray-400' : 'bg-green-600'}  text-white rounded-lg py-2 cursor-pointer`}
                                                disabled={isLoading || account?.paymentDetails?.disable_payment}
                                            >
                                                {account?.paymentDetails?.disable_payment ? 'Already subscribed' : (isLoading ? 'Processing...' : 'Renew Subscription')}
                                            </Button>

                                            <h4 className="text-lg font-semibold  text-left">Your Subscription: </h4>
                                            <p className="text-center">
                                                Your subscription expires on {" "}
                                                {DateTime.fromISO(account.paymentDetails.details.end_date).toFormat('d MMMM yyyy')}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-3">

                                            <Button
                                                onClick={handleUpgradeClick}
                                                className="w-full bg-green-600 text-white rounded-lg py-2 cursor-pointer"
                                                disabled={isLoading}
                                            >
                                                {isLoading ? 'Processing...' : 'Subscribe & Pay'}
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}

                        </div>
                    </Card>
                </Flex>
            </div>

            <AlertDialog.Root open={showEmailDialog} onOpenChange={setShowEmailDialog}>
                <AlertDialog.Content className="p-4 rounded shadow-lg w-1/3 mx-auto">
                    <AlertDialog.Title>Update Email Address</AlertDialog.Title>
                    <AlertDialog.Description>
                        Please provide an email address to continue with the payment.
                    </AlertDialog.Description>
                    <div className="mt-4">
                        <EmailUpdateForm setShowEmailDialog={setShowEmailDialog} />
                    </div>
                </AlertDialog.Content>
            </AlertDialog.Root>

            <ConfirmPaymentDialog
                showConfirmPaymentDialog={showConfirmPaymentDialog}
                setShowConfirmPaymentDialog={setShowConfirmPaymentDialog}
                discountCode={discountCode}
                setDiscountCode={setDiscountCode}
                isLoading={isLoading}
                applyDiscountCode={applyDiscountCode}
                handleDiscountOpenChange={handleConfirmPaymentOpenChange}
                selectedDuration={selectedDuration}
                setSelectedDuration={setSelectedDuration}
                baseAmount={baseAmount || 4000}
            />
        </>
    );
};

export default Payment;